import { Tabs } from "@/components/Tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { getUserID } from "@/lib/auth/auth-server-func";
import MainLayout from "@/lib/layout/MainLayout";
import { useCreatePoll } from "@/lib/tanstack-query/hooks/useCreatePoll";
import { CreatePollSchema } from "@/lib/zod-schemas/create-poll.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Dot, Plus, X } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute("/_dashboard/create-poll")({
  component: RouteComponent,
  beforeLoad: async () => {
    const userID = await getUserID();
    return { userID };
  },
  loader: async ({ context }) => {
    if (!context.userID) {
      throw redirect({ to: "/sign-in" });
    }
    return { userID: context.userID };
  },
});

function RouteComponent() {
  const { mutateAsync: createPoll, isPending: isLoadingCreate } =
    useCreatePoll();
  const form = useForm<z.infer<typeof CreatePollSchema>>({
    resolver: zodResolver(CreatePollSchema),
    defaultValues: {
      question: "",
      description: "",
      options: [{ value: "" }, { value: "" }],
      isNsfw: false,
    },
  });

  const currentIsNsfw = form.watch("isNsfw");

  const {
    fields: currentOptions,
    append,
    remove,
  } = useFieldArray<z.infer<typeof CreatePollSchema>>({
    control: form.control,
    name: "options",
  });

  const handleSubmit = async (value: z.infer<typeof CreatePollSchema>) => {
    await createPoll(value);
  };

  const [activeTab, setActiveTab] = useState<"create" | "rules">("create");

  return (
    <>
      <MainLayout.Header headline="Create Poll"></MainLayout.Header>
      <MainLayout.Sticky>
        <Tabs
          tabs={[
            { value: "create", label: "Create" },
            { value: "rules", label: "Rules" },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </MainLayout.Sticky>
      <MainLayout.Content>
        {activeTab === "create" ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4 dark:text-white"
            >
              <div className="space-y-2">
                {/* Question Field */}
                <FormField
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="shad-form_label">
                        Question
                      </FormLabel>
                      <FormControl>
                        <div>
                          <Textarea
                            className="min-h-0 rounded-xl"
                            placeholder="Would you rather...?"
                            {...field}
                          />
                          <div className="mt-2 flex items-center justify-between">
                            <FormMessage className="text-red-500" />
                            <span className="text-secondary-text-wu ml-auto text-sm">
                              {field.value.length}/1000
                            </span>
                          </div>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                ></FormField>

                {/* Description */}

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="shad-form_label">
                        Description
                      </FormLabel>
                      <FormControl>
                        <div>
                          <Textarea
                            placeholder="Add additional context or details about your poll..."
                            {...field}
                            className="rounded-xl"
                          />
                          <div className="mt-2 flex items-center justify-between">
                            <FormMessage className="text-red-500" />
                            <span className="text-secondary-text-wu ml-auto text-sm">
                              {field.value.length}/2000
                            </span>
                          </div>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                ></FormField>
              </div>

              {/* Poll Options */}
              <div className="mb-4">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {currentOptions.length}/10 options
                  </span>
                </div>
                {currentOptions.map((option, index) => (
                  <FormField
                    key={option.id}
                    control={form.control}
                    name={`options.${index}.value`}
                    render={({ field }) => (
                      <FormItem>
                        <div className="group flex flex-col">
                          <div className="flex items-center space-x-3">
                            <div className="w-full">
                              <div className="flex gap-2">
                                {/* <div className="bg-primary-wu flex w-8 items-center justify-center rounded-lg font-mono text-sm font-bold text-white">
                                  {index + 1}
                                </div> */}
                                <Textarea
                                  className="min-h-0 rounded-xl"
                                  {...form.register(`options.${index}.value`, {
                                    required: true,
                                  })}
                                  placeholder={`Option ${index + 1}`}
                                />
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <FormMessage className="text-red-500" />
                                <span className="text-secondary-text-wu ml-auto text-sm">
                                  {field.value.length}/400
                                </span>
                              </div>
                            </div>
                            {currentOptions.length > 2 && (
                              <button
                                aria-label="Remove option"
                                type="button"
                                onClick={() => remove(index)}
                                className="mb-7 cursor-pointer rounded-full p-2 text-red-500 opacity-0 transition-colors group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </FormItem>
                    )}
                  />
                ))}
                {currentOptions.length < 10 && (
                  <button
                    type="button"
                    onClick={() => append({ value: "" })}
                    className="dark:hover:bg-button-hover-background-wu hover:bg-button-hover-background-wu mt-4 flex w-full cursor-pointer items-center justify-center space-x-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-3 text-gray-600 transition-all duration-200 dark:border-gray-600 dark:text-white"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">Add Option</span>
                  </button>
                )}
              </div>

              {/* NSFW Toggle */}
              <div className="bg-gray-wu rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
                      NSFW Content
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Mark this poll as "Not Safe For Work" if it contains adult
                      content
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={currentIsNsfw}
                      onChange={(e) =>
                        form.setValue("isNsfw", e.target.checked)
                      }
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 peer-checked:bg-red-600 peer-focus:ring-4 peer-focus:ring-red-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-red-800"></div>
                  </label>
                </div>
              </div>
              {/* Submit Button */}
              <div className="flex items-center justify-end space-x-4">
                <Button
                  type="button"
                  variant={"outline"}
                  onClick={() => {
                    form.reset();
                  }}
                  className="dark:bg-button-background-wu dark:hover:bg-button-hover-background-wu"
                >
                  Reset
                </Button>

                <Button type="submit">Create Poll</Button>
              </div>
            </form>
          </Form>
        ) : (
          <Accordion type="multiple">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                Polls must contain "WYR" and and a short description in the
                title
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  Polls must contain "WYR" and and a short description in the
                  title All posts to this subreddit must contain
                </p>
                <ul className="list-none space-y-2 py-2">
                  <li className="flex items-center gap-2">
                    <Dot className="h-4 w-4" />
                    The phrase "WYR" or "would you rather" in the title
                  </li>
                  <li className="flex items-center gap-2">
                    <Dot className="h-4 w-4" />A short description of the
                    question and/or all poll options in the title
                  </li>
                </ul>
                <p>
                  The second point is necessary in order to prevent a bunch of
                  poll questions that only show up as "WYR"
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                Low effort shitposts are not allowed.
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  All submissions to wouldu.io are expected to contribute
                  meaningfully to the discussion and adhere to the would you
                  rather theme.
                </p>
                <p>
                  Posts that are purely for humor or low-effort content detract
                  from the quality of the community's discussions and are
                  subject to removal.
                </p>
                <p>
                  Questions where there is only one obvious good answer like
                  "WYR solve world hunger or die" is not a valid WYR question
                  for the site.
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No trivial preference-type questions
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  The crux of a good "WYR" question is that it forces the user
                  to think hard and pick between two or more similarly bad or
                  similarly good options.
                </p>
                <p>
                  Survey-type questions like "would you rather have chocolate or
                  vanilla?" is not a valid WYR question.
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No overly morbid or disturbing WouldYouRather's
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  Posts that are overly morbid or disturbing will be removed at
                  moderator's discretion. Body horror and pedophilia will be
                  removed under this rule.
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No game-show type questions
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  WouldYouRather is about discussion, and asking questions that
                  require a degree of contemplation. It’s not a game show.
                  Mentioning any kind of contest-type rules in your question is
                  lame.
                </p>
                <p>Examples of "game-show" questions:</p>
                <ul className="list-none space-y-2 py-2">
                  <li className="flex items-center gap-2">
                    <Dot className="h-4 w-4" />
                    Least/most picked choice wins
                  </li>
                  <li className="flex items-center gap-2">
                    <Dot className="h-4 w-4" />
                    Click random option, then see what you got.
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No politics.
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  This does not mean that all WYR questions about current topics
                  are banned, it just means that your question cannot be a
                  thinly disguised version of "WYR [agree with my political
                  view] or (horrible consequence that misrepresents my
                  opponent).
                </p>
                <p>Similarly, "Who WYR vote for" is not allowed.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-7">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No stereotyping or choosing between groups.
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  If your question involves stereotyping any group of people, it
                  will probably be deleted.
                </p>
                <p>
                  If your question involves asking people to choose out of
                  groups, it will probably be deleted. Example: "WYR prefer
                  [group X] or [group Y] as a co-worker" is not allowed
                </p>
                <p>
                  This includes posts of the type "which [insert area] would you
                  rather destroy"
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-8">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No advertisements
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>Advertising is not tolerated on this site.</p>
                <p>
                  Similarly, posts sneakily trying to advertise other apps by
                  saying "x% voted yes on xyz app" will be banned
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-9">
              <AccordionTrigger className="text-neutral-text hover:cursor-pointer">
                No reposts
              </AccordionTrigger>
              <AccordionContent className="text-neutral-text flex flex-col gap-2">
                <p>
                  Avoid reposting your Would You Rather question if it has been
                  posted within the last 6 months.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </MainLayout.Content>
    </>
  );
}
